import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import {
  signInWithCode,
  saveTokens,
  getStoredState,
  clearLoginState,
} from '../authUtils';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = React.useState<string | null>(null);
  const exchangedRef = React.useRef(false);

  React.useEffect(() => {
    // Guard against React 18 Strict Mode double-invocation — auth codes are single-use
    if (exchangedRef.current) return;
    exchangedRef.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (errorParam) {
      setError(errorDescription || errorParam);
      clearLoginState();
      return;
    }

    if (!code || !state) {
      setError('Missing authorization code or state. Please try signing in again.');
      clearLoginState();
      return;
    }

    const storedState = getStoredState();

    if (!storedState) {
      setError('Session expired. Please try signing in again.');
      clearLoginState();
      return;
    }

    if (state !== storedState) {
      setError('Invalid state. Please try signing in again.');
      clearLoginState();
      return;
    }

    // The backend handles the Keycloak token exchange using its client secret
    signInWithCode(code)
      .then((response) => {
        saveTokens(response);
        clearLoginState();
        navigate('/dashboard', { replace: true });
      })
      .catch((err) => {
        setError(err.message || 'Authentication failed. Please try again.');
        clearLoginState();
      });
  }, [searchParams, navigate]);

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <Typography color="error">{error}</Typography>
        <Typography
          component="a"
          href="/"
          sx={{ color: 'primary.main', textDecoration: 'underline' }}
        >
          Back to sign in
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography color="text.secondary">Completing sign in...</Typography>
    </Box>
  );
}
